import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import Index from "./pages/Index";
import Indents from "./pages/Indents";
import PurchaseOrders from "./pages/PurchaseOrders";
import CreatePO from "./pages/CreatePO";
import POApproval from "./pages/POApproval";
import VendorOrders from "./pages/VendorOrders";
import GRNVerification from "./pages/GRNVerification";
import StockInventory from "./pages/StockInventory";
import Recipes from "./pages/Recipes";
import TVDashboard from "./pages/TVDashboard";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/auth/login";
import ForgotPasswordPage from "./pages/auth/ForgotPassword";
import ResetPasswordPage from "./pages/auth/ResetPassword";
import UnitMaster from "./pages/UnitMaster";
import AddUnit from "./pages/AddUnit";
import AddUnitConversionRule from "./pages/AddUnitConversionRule";
import Departments from "./pages/Departments";
import CreateIndent from "./pages/CreateIndent.js";
import VendorMaster from "./pages/VendorMaster.js";
import ViewIngredient from "./pages/IngredientView.js";
import MenuCategory from "./pages/MenuCategory.js";
import ItemVariations from "./pages/ItemVariations.js";
import ItemAddons from "./pages/ItemAddons.js";
import AddEditAddon from "./pages/AddAddons.js";
import MenuItems from "./pages/Items.js";
import AddEditMenuItem from "./pages/AddEditItems.js";
import Employees from "./pages/Employees.js";
import AddEditStaff from "./pages/AddEditStaff.js";
import RolesPermissions from "./pages/RolesPermissions.js";
import AddEditRole from "./pages/AddEditRoles.js";
import OutletSettings from "./pages/OutletSettings.js";
import TableManagement from "./pages/TableManagement.js";
import AreaManagement from "./pages/AreaManagement.js";
import AddEditTable from "./pages/AddEditTable.js";
import AddEditArea from "./pages/AddEditArea.js";
import StoreManagement from "./pages/StoreManagement.js";
import StaffRights from "./pages/ViewPermissions.js";
import SubStaffRights from "./pages/Substaffrights.js";
import HelpSupport from "./pages/HelpSupport.js";
import InsidePageRights from "./pages/InsidePageRights.js";
import CRM from "./pages/CRM.js";
import Reservations from "./pages/Reservations.js";
import DayEndReport from "./pages/DayendReports.js";
import SalesReport from "./pages/SalesReport.js";
import KotSalesReport from "./pages/KotSalesReport.js";
import ItemWiseSalesReport from "./pages/ItemsSalesReport.js";
import TipReport from "./pages/TipReports.js";
import DiscountReport from "./pages/DiscountReports.js";
import CustomerWiseReport from "./pages/CustomerWiseReport.js";
import ViewRecipe from "./pages/Recipies.js";
import OrderBilling from "./pages/OrderBilling.js";
import AddEditRecipe from "./pages/AddEditRecipe.js";
import StockInventoryReports from "./pages/StockInventryReports.js";
import IndentsReports from "./pages/IndentReports.js";
import GrnReport from "./pages/GrnReport.js";
import AuditScreen from "./pages/Auditscreen.js";
import AuditList from "./pages/AuditList.js";
import AdjustmentScreen from "./pages/AdjustmentScreen.js";
import AdjustmentList from "./pages/AdjustmentList.js";
import { useEffect } from "react";
import QrGenerate from "./pages/QrGenrate.js";
import MasterReport from "./pages/MasterReport.js";
import OrderModificationReport from "./pages/OrderModificationReport.js";
import ComboRules from "./pages/ComboRules.js";
import ZomatoManagement from "./pages/ZomatoManagement.jsx";
import KotModificationReport from "./pages/KotModificationReport.js";
import ComplimentaryReport from "./pages/ComplimentaryReport.js";
import PaymentSummaryReport from "./pages/PaymentSummaryReport.js";
// Stock Reports
import StockLedgerReport from "./pages/StockLedgerReport.js";
import StockSummaryReport from "./pages/StockSummaryReport.js";
import StockIssueReport from "./pages/StockIssueReport.js";
import AuditReport from "./pages/AuditReport.js";
import AdjustmentReport from "./pages/AdjustmentReport.js";
// Purchase & Kitchen Reports
import PurchaseOrderReport from "./pages/PurchaseOrderReport.js";
import IndentReport from "./pages/IndentReport.js";
// Consumption & Costing Reports
import ConsumptionReport from "./pages/ConsumptionReport.js";
import RecipeCostingReport from "./pages/RecipeCostingReport.js";
const queryClient = new QueryClient();

const App = () => {
  // Global: Prevent number input scroll behavior and arrow key changes
  useEffect(() => {
    // Prevent scroll from changing number input values
    const handleWheel = (e) => {
      if (document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    };

    // Prevent arrow up/down keys from changing number input values
    const handleKeyDown = (e) => {
      if (
        document.activeElement.type === "number" &&
        (e.key === "ArrowUp" || e.key === "ArrowDown")
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
      >
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/Dashboard" element={<Index />} />
              <Route path="/tv-dashboard" element={<TVDashboard />} />
              <Route path="/orders" element={<OrderBilling />} />
              <Route path="/crm" element={<CRM />} />
              <Route path="/reservations" element={<Reservations />} />
              {/* Unit Master Pages */}
              <Route path="/view-units" element={<UnitMaster />} />
              <Route
                path="/add-conversion-rule/:unitId"
                element={<AddUnitConversionRule />}
              />
              <Route path="/add-units" element={<AddUnit />} />
              {/* Indent Pages */}
              <Route path="/indents" element={<Indents />} />
              <Route path="/create-indent" element={<CreateIndent />} />
              {/* Departments Pages */}
              <Route path="/departments" element={<Departments />} />
              {/* Store Managment */}
              <Route path="/store-managment" element={<StoreManagement />} />
              {/* Purchase Orders Pages */}
              <Route path="/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/create-po" element={<CreatePO />} />
              <Route path="/approval-po" element={<POApproval />} />
              {/* vendor Master Pages */}
              <Route path="/vendor-master" element={<VendorMaster />} />
              <Route path="/vendor-orders" element={<VendorOrders />} />
              <Route path="/grn" element={<GRNVerification />} />
              <Route path="/inventory" element={<StockInventory />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/ingredient-view" element={<ViewIngredient />} />
              <Route path="/recipe-view" element={<ViewRecipe />} />
              <Route path="/add-recipe" element={<AddEditRecipe />} />
              <Route path="/edit-recipe/:id" element={<AddEditRecipe />} />
              <Route path="/audits" element={<AuditList />} />
              <Route path="/audit-create" element={<AuditScreen />} />
              <Route path="/adjustment-create" element={<AdjustmentScreen />} />
              <Route path="/adjustments" element={<AdjustmentList />} />

              {/* Menu Managment Pages */}
              <Route path="/menu-category" element={<MenuCategory />} />
              <Route path="/item-variation" element={<ItemVariations />} />
              <Route path="/item-addons" element={<ItemAddons />} />
              <Route path="/add-addon" element={<AddEditAddon />} />
              <Route path="/edit-addon" element={<AddEditAddon />} />
              <Route path="/items" element={<MenuItems />} />
              <Route path="/combo-rules" element={<ComboRules />} />
              <Route path="/add-item" element={<AddEditMenuItem />} />
              <Route path="/edit-item" element={<AddEditMenuItem />} />

              {/* Staff Management Pages */}
              <Route path="/staff-management" element={<Employees />} />
              <Route path="/add-staff" element={<AddEditStaff />} />
              <Route path="/edit-staff/:id" element={<AddEditStaff />} />
              <Route path="/roles" element={<RolesPermissions />} />
              <Route path="/add-roles" element={<AddEditRole />} />
              <Route path="/edit-roles/:id" element={<AddEditRole />} />
              <Route path="/view-permissions" element={<StaffRights />} />
              <Route
                path="/view-sub-permissions/:id"
                element={<SubStaffRights />}
              />
              <Route
                path="/inside-pagerights/:id"
                element={<InsidePageRights />}
              />

              {/* Outlet settings page */}
              <Route path="/outlet-settings" element={<OutletSettings />} />

              {/* Reports page */}
              <Route path="/master-report" element={<MasterReport />} />
              <Route path="/day-end-reports" element={<DayEndReport />} />
              <Route path="/sales-reports" element={<SalesReport />} />
              <Route path="/kot-sales-reports" element={<KotSalesReport />} />
              <Route
                path="/items-sales-reports"
                element={<ItemWiseSalesReport />}
              />

{/* Stock Related Reports */}
              <Route path="/stock-ledger" element={<StockLedgerReport />} />
              <Route path="/stock-summary" element={<StockSummaryReport />} />
              <Route path="/stock-issue" element={<StockIssueReport />} />
              <Route path="/audit" element={<AuditReport />} />
              <Route path="/adjustment" element={<AdjustmentReport />} />

              {/* Purchase & Kitchen Reports */}
              <Route path="/purchase-order" element={<PurchaseOrderReport />} />
              <Route path="/grn-report" element={<GrnReport />} />
              <Route path="/indent" element={<IndentReport />} />

              {/* Consumption & Costing Reports */}
              <Route path="/consumption" element={<ConsumptionReport />} />
              <Route path="/recipe-costing" element={<RecipeCostingReport />} />

              <Route path="/tip-reports" element={<TipReport />} />
              <Route path="/discount-reports" element={<DiscountReport />} />
              <Route path="/complimentary-reports" element={<ComplimentaryReport/>} />
              <Route path="/payment-summary-reports" element={<PaymentSummaryReport/>} />
              <Route path="/customer-wise-reports" element={<CustomerWiseReport />} />
              <Route path="/order-modification-reports" element={<OrderModificationReport />} />
              <Route path="/kot-modification-reports" element={<KotModificationReport />} />
              <Route
                path="/stock-inventry-reports"
                element={<StockInventoryReports />}
              />
              <Route path="/indents-reports" element={<IndentsReports />} />

              {/* Restaurant Management page */}
              <Route path="/table-management" element={<TableManagement />} />
              <Route path="/add-table" element={<AddEditTable />} />
              <Route path="/edit-table" element={<AddEditTable />} />
              <Route path="/area-management" element={<AreaManagement />} />
              <Route path="/add-area" element={<AddEditArea />} />
              <Route path="/edit-area" element={<AddEditArea />} />
              <Route path="/zomato-management" element={<ZomatoManagement />} />
              <Route path="/help" element={<HelpSupport />} />
              {/* Generate Qr Page */}
              <Route path="/QrGenrate" element={<QrGenerate />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
